import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, MarkerF, useJsApiLoader } from '@react-google-maps/api';
import { useNavigate } from 'react-router-dom';
import BottomTabNavigation from '../../../components/openplay/BottomTabNavigation';
import MapFilterToolbar from '../../../components/openplay/MapFilterToolbar';
import ContextualActionSheet from '../../../components/openplay/ContextualActionSheet';
import LocationSearchBar from './LocationSearchBar';
import MapViewToggle from './MapViewToggle';
import EventListView from './EventListView';
import EventCard from './EventCard';
import ApplicationModal from './ApplicationModal';
import InquiryModal from './InquiryModal';
import Icon from '../../../components/openplay/OpenPlayIcon';

const InteractiveEventMapClient = () => {
  const navigate = useNavigate();
  const [isHydrated, setIsHydrated] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentView, setCurrentView] = useState('map');
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [filterState, setFilterState] = useState({
    dateRange: { start: null, end: null },
    availability: 'all',
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem('openplay_sidebar_collapsed');
      setSidebarCollapsed(saved === '1');
    } catch {
      setSidebarCollapsed(false);
    }

    const handler = (e) => {
      setSidebarCollapsed(!!e?.detail?.collapsed);
    };

    try {
      window.addEventListener('openplay_sidebar_collapsed_change', handler);
    } catch {
      // ignore
    }

    return () => {
      try {
        window.removeEventListener('openplay_sidebar_collapsed_change', handler);
      } catch {
        // ignore
      }
    };
  }, []);

  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const { isLoaded: isGoogleMapsLoaded, loadError: googleMapsLoadError } = useJsApiLoader({
    googleMapsApiKey: googleMapsApiKey || '',
  });

  const googleMapRef = useRef(null);

  const mockEvents = [
    {
      id: '1',
      title: '주말 테니스 오픈 매치',
      organizationName: '강남테니스클럽',
      organizationLogo: 'https://images.unsplash.com/photo-1684443726772-41e78c5d1e23',
      organizationLogoAlt: 'Tennis racket and ball on green court surface',
      sportType: 'tennis',
      date: '2026.01.12',
      time: '14:00',
      location: '강남구 테니스장',
      latitude: 37.4979,
      longitude: 127.0276,
      participantCount: 6,
      maxParticipants: 8,
      skillLevel: '중급',
      distance: '1.2km',
    },
    {
      id: '2',
      title: '배드민턴 복식 경기',
      organizationName: '서울배드민턴',
      organizationLogo: 'https://images.unsplash.com/photo-1723581637176-004e8dac2c49',
      organizationLogoAlt: 'Badminton shuttlecock and racket on wooden floor',
      sportType: 'badminton',
      date: '2026.01.13',
      time: '10:00',
      location: '송파구 체육관',
      latitude: 37.5145,
      longitude: 127.1059,
      participantCount: 8,
      maxParticipants: 8,
      skillLevel: '고급',
      distance: '3.5km',
    },
    {
      id: '3',
      title: '축구 친선 경기',
      organizationName: '마포FC',
      organizationLogo: 'https://images.unsplash.com/photo-1634114441107-7bc1d35dafd3',
      organizationLogoAlt: 'Soccer ball on green grass field with goal post in background',
      sportType: 'soccer',
      date: '2026.01.14',
      time: '16:00',
      location: '마포구 축구장',
      latitude: 37.5665,
      longitude: 126.9018,
      participantCount: 15,
      maxParticipants: 22,
      skillLevel: '초급',
      distance: '2.8km',
    },
    {
      id: '4',
      title: '농구 3on3 토너먼트',
      organizationName: '강북농구클럽',
      organizationLogo: 'https://images.unsplash.com/photo-1556300673-04df21735615',
      organizationLogoAlt: 'Orange basketball on outdoor court with hoop visible',
      sportType: 'basketball',
      date: '2026.01.15',
      time: '18:00',
      location: '강북구 농구장',
      latitude: 37.6396,
      longitude: 127.0254,
      participantCount: 10,
      maxParticipants: 12,
      skillLevel: '중급',
      distance: '5.1km',
    },
  ];

  const filteredEvents = mockEvents.filter((event) => {
    if (filterState.availability === 'available' && event.participantCount >= event.maxParticipants) {
      return false;
    }
    if (filterState.availability === 'full' && event.participantCount < event.maxParticipants) {
      return false;
    }
    return true;
  });

  const mapCenter = useMemo(() => {
    if (filteredEvents.length > 0) {
      return { lat: filteredEvents[0].latitude, lng: filteredEvents[0].longitude };
    }
    return { lat: 37.5665, lng: 126.9780 };
  }, [filteredEvents]);

  useEffect(() => {
    if (!googleMapRef.current) return;
    if (!isGoogleMapsLoaded) return;
    if (!window.google?.maps) return;

    if (filteredEvents.length === 0) {
      googleMapRef.current.setCenter(mapCenter);
      googleMapRef.current.setZoom(11);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    filteredEvents.forEach((event) => {
      bounds.extend({ lat: event.latitude, lng: event.longitude });
    });
    googleMapRef.current.fitBounds(bounds);
  }, [filteredEvents, isGoogleMapsLoaded, mapCenter]);

  const selectedEvent = selectedEventId ? mockEvents.find((e) => e.id === selectedEventId) : null;

  const handleMarkerClick = (eventId) => {
    setSelectedEventId(eventId);
    setShowBottomSheet(true);
  };

  const handleApply = (eventId) => {
    setSelectedEventId(eventId);
    setShowApplicationModal(true);
    setShowBottomSheet(false);
  };

  const handleInquiry = (eventId) => {
    setSelectedEventId(eventId);
    setShowInquiryModal(true);
    setShowBottomSheet(false);
  };

  const handleViewDetails = (eventId) => {
    navigate(`/openplay/event-details?id=${eventId}`);
  };

  const handleApplicationSubmit = (data) => {
    console.log('Application submitted:', data);
    setShowApplicationModal(false);
    setSelectedEventId(null);
  };

  const handleInquirySubmit = (message) => {
    console.log('Inquiry submitted:', message);
    setShowInquiryModal(false);
    setSelectedEventId(null);
  };

  const handleSearch = (query) => {
    console.log('Search query:', query);
  };

  const handleUseCurrentLocation = () => {
    console.log('Using current location');
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground caption">지도를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className={sidebarCollapsed ? 'md:pl-[72px]' : 'md:pl-[240px]'}>
        <MapFilterToolbar filterState={filterState} onFilterChange={setFilterState} />

        <div className="relative">
          <div className="sticky top-0 z-sticky bg-card shadow-elevation-1 md:top-[72px]">
            <div className="max-w-7xl mx-auto px-6 py-4">
              <div className="flex items-center gap-4">
                <LocationSearchBar onSearch={handleSearch} onUseCurrentLocation={handleUseCurrentLocation} />
                <MapViewToggle currentView={currentView} onToggle={setCurrentView} />
              </div>
            </div>
          </div>

          {currentView === 'map' && (
            <div className="relative h-[calc(100dvh-200px-76px)] md:h-[calc(100vh-216px)] bg-muted">
              {!googleMapsApiKey && (
                <div className="absolute inset-0 flex items-center justify-center p-6">
                  <div className="max-w-md w-full bg-card rounded-xl border border-border shadow-elevation-2 p-6 text-center space-y-3">
                    <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon name="MapIcon" size={32} className="text-primary" />
                    </div>
                    <p className="text-base font-semibold text-foreground">Google Maps API Key가 필요합니다</p>
                    <p className="text-sm text-muted-foreground caption">
                      <code className="px-1.5 py-0.5 rounded bg-muted text-foreground">.env</code>에
                      <code className="px-1.5 py-0.5 rounded bg-muted text-foreground ml-2">VITE_GOOGLE_MAPS_API_KEY</code>를 설정한 뒤
                      dev server를 재시작하세요.
                    </p>
                  </div>
                </div>
              )}

              {googleMapsLoadError && (
                <div className="absolute inset-0 flex items-center justify-center p-6">
                  <div className="max-w-md w-full bg-card rounded-xl border border-border shadow-elevation-2 p-6 text-center space-y-3">
                    <p className="text-base font-semibold text-foreground">지도를 불러오지 못했습니다</p>
                    <p className="text-sm text-muted-foreground caption">
                      Google Maps JavaScript API가 활성화되었는지, API Key 제한(도메인 제한)이 맞는지 확인해주세요.
                    </p>
                    <div className="text-left text-xs text-muted-foreground caption bg-muted rounded-lg p-3 space-y-1">
                      <div className="break-all">
                        <span className="font-semibold text-foreground">error:</span> {googleMapsLoadError?.message}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {googleMapsApiKey && !isGoogleMapsLoaded && !googleMapsLoadError && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm text-muted-foreground caption">지도를 불러오는 중...</p>
                  </div>
                </div>
              )}

              {googleMapsApiKey && isGoogleMapsLoaded && !googleMapsLoadError && (
                <div className="absolute inset-0">
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={mapCenter}
                    zoom={11}
                    options={{
                      mapTypeControl: false,
                      fullscreenControl: false,
                      streetViewControl: false,
                      clickableIcons: false,
                    }}
                    onLoad={(map) => {
                      googleMapRef.current = map;
                    }}
                    onUnmount={() => {
                      googleMapRef.current = null;
                    }}
                  >
                    {filteredEvents.map((event) => (
                      <MarkerF
                        key={event.id}
                        position={{ lat: event.latitude, lng: event.longitude }}
                        title={event.organizationName}
                        onClick={() => handleMarkerClick(event.id)}
                      />
                    ))}
                  </GoogleMap>
                </div>
              )}

              <div className="absolute top-4 left-4 px-4 py-2 rounded-lg bg-card shadow-elevation-2 pointer-events-none">
                <p className="text-sm font-medium text-foreground">이벤트 {filteredEvents.length}개</p>
              </div>
            </div>
          )}

          {currentView === 'list' && (
            <div className="min-h-[calc(100dvh-200px-76px)] md:min-h-[calc(100vh-216px)] bg-background">
              <EventListView
                events={filteredEvents}
                onApply={handleApply}
                onInquiry={handleInquiry}
                onViewDetails={handleViewDetails}
              />
            </div>
          )}
        </div>
      </div>

      <ContextualActionSheet
        isOpen={showBottomSheet}
        onClose={() => {
          setShowBottomSheet(false);
          setSelectedEventId(null);
        }}
        height="auto"
        showCloseButton={false}
      >
        {selectedEvent && (
          <EventCard
            event={selectedEvent}
            onApply={() => handleApply(selectedEvent.id)}
            onInquiry={() => handleInquiry(selectedEvent.id)}
            onViewDetails={() => handleViewDetails(selectedEvent.id)}
          />
        )}
      </ContextualActionSheet>

      {selectedEvent && (
        <ApplicationModal
          isOpen={showApplicationModal}
          onClose={() => {
            setShowApplicationModal(false);
            setSelectedEventId(null);
          }}
          eventTitle={selectedEvent.title}
          onSubmit={handleApplicationSubmit}
        />
      )}

      {selectedEvent && (
        <InquiryModal
          isOpen={showInquiryModal}
          onClose={() => {
            setShowInquiryModal(false);
            setSelectedEventId(null);
          }}
          eventTitle={selectedEvent.title}
          organizationName={selectedEvent.organizationName}
          onSubmit={handleInquirySubmit}
        />
      )}

      <BottomTabNavigation userRole="player" />
    </div>
  );
};

export default InteractiveEventMapClient;
